import { Box, useMediaQuery, useTheme, Chip, Typography } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import BadgeIcon from '@mui/icons-material/Badge'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import EventIcon from '@mui/icons-material/Event'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { APP_CONSTANTS } from 'src/consumer/constants'
import logo_brand from 'src/assets/logo_brand.png'
import { getPriceStringWithCurrency, getCurrencyObject } from 'src/mentor/helpers/utilityFunctions'
import {
  LiveSessionHeaderBottom,
  LiveSessionHeaderTop,
  useBottomHeadingText,
  useDebugMode,
  useTopHeadingFirstText,
} from 'src/consumer/components/LiveSessionPage/hooks/useLiveSessionHeaderHooks'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'

dayjs.extend(utc)
dayjs.extend(timezone)
import {
  GENDER_LIST_ONBOARDING,
  EVENT_TYPE_THERAPY_SESSION,
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_ASSESSMENT,
} from 'src/consumer/constants'
import { getOrdinalSuffix } from 'src/consumer/helpers/utilFunctions'

export const LiveSessionHeaderDetails = ({ bookedEventFinalData, auxiliaryData }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const handleDebugModeClick = useDebugMode()
  const prescriptionData = auxiliaryData?.metaPrescription
  const therapyReportData = auxiliaryData?.therapyReport
  const screeningReportData = auxiliaryData?.screeningReport
  const assessmentReportData = auxiliaryData?.assessmentReport
  const consultationDetails = auxiliaryData?.consultationDetails
  const patientVisitSummary = auxiliaryData?.patientVisitSummary
  const hasReportData = prescriptionData || therapyReportData || screeningReportData || assessmentReportData
  const isOffline = bookedEventFinalData?.meetingSrc === 'offline'

  const topHeadingFirstText = useTopHeadingFirstText({
    eventType: bookedEventFinalData?.eventType,
    prescriptionData,
    therapyReport: therapyReportData,
    screeningReport: screeningReportData,
    assessmentReport: assessmentReportData,
    bookedEventFinalData,
  })

  const bottomHeadingText = useBottomHeadingText({
    eventType: bookedEventFinalData?.eventType,
    prescriptionData,
    therapyReport: therapyReportData,
    screeningReport: screeningReportData,
    assessmentReport: assessmentReportData,
    consultationDetails,
    startTime: bookedEventFinalData?.startTime,
    endTime: bookedEventFinalData?.endTime,
    isOffline,
  })

  // Derive assessment title for display (e.g. "PHQ-9", "GAD-7", "Assessment Report")
  const assessmentTitle =
    assessmentReportData?.assessmentRuns?.[0]?.clinicalAssessmentTitle ??
    assessmentReportData?.assessmentRuns?.[0]?.psychAssessmentTitle ??
    assessmentReportData?.assessmentRuns?.[0]?.assessmentTitle ??
    assessmentReportData?.title ??
    (bookedEventFinalData?.eventType === EVENT_TYPE_ASSESSMENT ? 'Assessment' : null)

  // Get patient details for therapy report, consultation, or assessment
  const getPatientDetails = () => {
    // For assessment sessions (use assessmentReport patient when available, else bookedEvent patient)
    if (bookedEventFinalData?.eventType === EVENT_TYPE_ASSESSMENT) {
      const report = assessmentReportData
      const patientName =
        report?.patientName ??
        bookedEventFinalData?.patientName ??
        bookedEventFinalData?.patientPrefill?.name
      const patientNumber =
        report?.patientNumber ?? bookedEventFinalData?.patientNumber ?? bookedEventFinalData?.patientPrefill?.patientNumber
      const dob = report?.patientDateOfBirth ?? bookedEventFinalData?.patientDateOfBirth ?? bookedEventFinalData?.patientPrefill?.vital?.dateOfBirth
      const gender = report?.patientGender ?? bookedEventFinalData?.patientGender ?? bookedEventFinalData?.patientPrefill?.vital?.gender
      let visitNumber = patientVisitSummary?.sessionsCompletedForCurrentEventTypeForCurrentMentor
      if (visitNumber != null) visitNumber += 1
      else visitNumber = 1
      const visitNumberText = visitNumber ? getOrdinalSuffix(visitNumber) : null
      return {
        name: patientName ?? '—',
        number: patientNumber ?? null,
        age: dob ? getAgeFromDobWithTime(dob) : null,
        gender: gender
          ? GENDER_LIST_ONBOARDING?.find((item) => item.value === gender)?.label ?? gender
          : null,
        visitNumber: visitNumberText,
        date: bookedEventFinalData?.startTime
          ? dayjs.utc(bookedEventFinalData.startTime).local().format('DD MMM YYYY')
          : null,
        time:
          bookedEventFinalData?.startTime && bookedEventFinalData?.endTime
            ? `${dayjs.utc(bookedEventFinalData.startTime).local().format('h:mma')} - ${dayjs
                .utc(bookedEventFinalData.endTime)
                .local()
                .format('h:mma')}`
            : null,
        feePaid: bookedEventFinalData?.feePaid ?? null,
        currency: bookedEventFinalData?.currency ?? null,
      }
    }

    // For therapy sessions
    if (bookedEventFinalData?.eventType === EVENT_TYPE_THERAPY_SESSION && therapyReportData) {
      // Calculate visit number
      let visitNumber = patientVisitSummary?.sessionsCompletedForCurrentEventTypeForCurrentMentor
      if (visitNumber) {
        visitNumber += 1 // Adding current visit
      } else {
        visitNumber = 1
      }
      const visitNumberText = visitNumber ? getOrdinalSuffix(visitNumber) : null

      return {
        name: therapyReportData?.patientName,
        number: therapyReportData?.patientNumber,
        age: therapyReportData?.patientDateOfBirth
          ? getAgeFromDobWithTime(therapyReportData.patientDateOfBirth)
          : null,
        gender: therapyReportData?.patientGender
          ? GENDER_LIST_ONBOARDING?.find(
              (item) => item.value === therapyReportData.patientGender,
            )?.label
          : null,
        visitNumber: visitNumberText,
        date: bookedEventFinalData?.startTime
          ? dayjs.utc(bookedEventFinalData.startTime).local().format('DD MMM YYYY')
          : null,
        time: bookedEventFinalData?.startTime && bookedEventFinalData?.endTime
          ? `${dayjs.utc(bookedEventFinalData.startTime).local().format('h:mma')} - ${dayjs
              .utc(bookedEventFinalData.endTime)
              .local()
              .format('h:mma')}`
          : null,
        feePaid: null,
        currency: null,
      }
    }
    
    // For consultations (prescription making page)
    if (bookedEventFinalData?.eventType === EVENT_TYPE_CONSULTATION && prescriptionData) {
      // Calculate visit number
      let visitNumber = patientVisitSummary?.sessionsCompletedForCurrentEventTypeForCurrentMentor
      if (visitNumber) {
        visitNumber += 1 // Adding current visit
      } else {
        visitNumber = 1
      }
      const visitNumberText = visitNumber ? getOrdinalSuffix(visitNumber) : null

      return {
        name: prescriptionData?.patientPrefill?.name,
        number: prescriptionData?.patientPrefill?.patientNumber,
        age: prescriptionData?.patientPrefill?.vital?.dateOfBirth
          ? getAgeFromDobWithTime(prescriptionData.patientPrefill.vital.dateOfBirth)
          : null,
        gender: prescriptionData?.patientPrefill?.vital?.gender
          ? GENDER_LIST_ONBOARDING?.find(
              (item) => item.value === prescriptionData.patientPrefill.vital.gender,
            )?.label
          : null,
        visitNumber: visitNumberText,
        date: bookedEventFinalData?.startTime
          ? dayjs.utc(bookedEventFinalData.startTime).local().format('DD MMM YYYY')
          : null,
        time: bookedEventFinalData?.startTime && bookedEventFinalData?.endTime
          ? `${dayjs.utc(bookedEventFinalData.startTime).local().format('h:mma')} - ${dayjs
              .utc(bookedEventFinalData.endTime)
              .local()
              .format('h:mma')}`
          : null,
        feePaid: bookedEventFinalData?.feePaid,
        currency: bookedEventFinalData?.currency,
        mobile: prescriptionData?.patientPrefill?.mobile,
        email: prescriptionData?.patientPrefill?.email,
      }
    }
    
    return null
  }

  const patientDetails = getPatientDetails()
  // Show the structured patient block whenever we have details — a compact name + age/gender/mobile
  // line on mobile, the full chip layout on desktop.
  const showPatientDetails = Boolean(patientDetails)
  const feeDisplay = patientDetails?.feePaid
    ? `${getCurrencyObject(patientDetails.currency, true)?.symbol || ''}${new Intl.NumberFormat(
        'en-IN',
      ).format(patientDetails.feePaid)}`
    : null

  return (
    <GenericDisplayFlex
      style={{
        height: '100%',
        alignItems: 'center',
        gap: '16px',
        flex: 1,
        minWidth: 0,
      }}
    >
      {!hasReportData && (
        <img
          src={APP_CONSTANTS.APP_LOGO_LINK}
          style={{
            height: '40px',
            width: '44px',
            borderRadius: '8px',
            flexShrink: 0,
            objectFit: 'contain',
          }}
          alt="logo"
          onError={(e) => {
            e.target.src = logo_brand
          }}
        />
      )}

      {showPatientDetails ? (
        isMobile ? (
          /* Compact mobile patient block: name + fee on top; age · gender · patient-no · mobile
             below. Keeps the key context in the header so the duplicate patient card below can go. */
          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#ffffff',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
                  minWidth: 0,
                }}
              >
                {patientDetails.name}
              </Typography>
              {feeDisplay && (
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#ffffff',
                    lineHeight: 1,
                    px: '7px',
                    py: '3px',
                    borderRadius: '7px',
                    background: 'rgba(16, 185, 129, 0.32)',
                    border: '1px solid rgba(16, 185, 129, 0.45)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {feeDisplay}
                </Box>
              )}
            </Box>
            {(patientDetails.age ||
              patientDetails.gender ||
              patientDetails.number ||
              patientDetails.mobile) && (
              <Typography
                sx={{
                  fontSize: '11.5px',
                  fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {[
                  [patientDetails.age && `${patientDetails.age}Y`, patientDetails.gender]
                    .filter(Boolean)
                    .join(' '),
                  patientDetails.number && `#${patientDetails.number}`,
                  patientDetails.mobile,
                ]
                  .filter(Boolean)
                  .join('  ·  ')}
              </Typography>
            )}
          </Box>
        ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: '0 0 auto', alignSelf: 'center' }}>
          {/* Assessment title – show clearly when this is an assessment session */}
          {assessmentTitle && (
            <Chip
              label={assessmentTitle}
              size="small"
              sx={{
                alignSelf: 'flex-start',
                fontWeight: 700,
                fontSize: { xs: '12px', md: '13px' },
                color: '#fff',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(79, 70, 229, 0.9) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                boxShadow: '0 2px 6px rgba(99, 102, 241, 0.35)',
                '& .MuiChip-label': { px: 1.25 },
              }}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
              width: 'fit-content',
              maxWidth: { xs: '220px', sm: '320px', md: '400px', lg: '480px' },
              padding: { xs: '3px 10px', md: '4px 14px' },
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.12) 100%)',
              backdropFilter: 'blur(12px) saturate(180%)',
              WebkitBackdropFilter: 'blur(12px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0.16) 100%)',
                borderColor: 'rgba(255, 255, 255, 0.25)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
              },
            }}
          >
          {/* Line 1: Patient Name (Large) | Patient Number | Age/Gender */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '6px', md: '10px' },
              flexWrap: 'wrap',
            }}
          >
            {/* Patient Name - Large Font */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                minWidth: 0,
              }}
            >
              <PersonIcon
                sx={{
                  fontSize: { xs: '18px', md: '20px' },
                  color: 'rgba(255, 255, 255, 0.98)',
                  flexShrink: 0,
                  filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
                }}
              />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '14px', md: '16px', lg: '17px' },
                  color: '#ffffff',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: { xs: '140px', md: '200px', lg: '240px' },
                  lineHeight: 1.2,
                  letterSpacing: '0.1px',
                  textShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
                }}
              >
                {patientDetails.name}
              </Typography>
            </Box>

            {/* Patient Number */}
            {patientDetails.number && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  paddingLeft: { xs: '8px', md: '12px' },
                  borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
                  height: '16px',
                }}
              >
                <BadgeIcon
                  sx={{
                    fontSize: { xs: '13px', md: '14px' },
                    color: 'rgba(255, 255, 255, 0.88)',
                    opacity: 0.9,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: '11px', md: '12px' },
                    color: 'rgba(255, 255, 255, 0.92)',
                    fontWeight: 500,
                    letterSpacing: '0.2px',
                    lineHeight: 1,
                  }}
                >
                  {patientDetails.number}
                </Typography>
              </Box>
            )}

            {/* Age/Gender */}
            {(patientDetails.age || patientDetails.gender) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: { xs: '8px', md: '12px' },
                  borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
                  height: '16px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '11px', md: '12px' },
                    color: 'rgba(255, 255, 255, 0.88)',
                    fontWeight: 500,
                    letterSpacing: '0.2px',
                    lineHeight: 1,
                  }}
                >
                  {[patientDetails.age && `${patientDetails.age}Y`, patientDetails.gender]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Line 2: Visit Number | Date | Time | Fee */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '5px', md: '8px' },
              flexWrap: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {/* Visit Number */}
            {patientDetails.visitNumber && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexShrink: 0,
                }}
              >
                <EventIcon
                  sx={{
                    fontSize: { xs: '12px', md: '13px' },
                    color: 'rgba(255, 255, 255, 0.85)',
                    opacity: 0.9,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: '11px', md: '12px' },
                    color: 'rgba(255, 255, 255, 0.92)',
                    fontWeight: 500,
                    letterSpacing: '0.2px',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {patientDetails.visitNumber} Visit
                </Typography>
              </Box>
            )}

            {/* Date */}
            {patientDetails.date && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  paddingLeft: { xs: '6px', md: '10px' },
                  borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
                  height: '16px',
                  flexShrink: 0,
                }}
              >
                <CalendarTodayIcon
                  sx={{
                    fontSize: { xs: '12px', md: '13px' },
                    color: 'rgba(255, 255, 255, 0.85)',
                    opacity: 0.9,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: '11px', md: '12px' },
                    color: 'rgba(255, 255, 255, 0.92)',
                    fontWeight: 500,
                    letterSpacing: '0.2px',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {patientDetails.date}
                </Typography>
              </Box>
            )}

            {/* Time */}
            {patientDetails.time && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  paddingLeft: { xs: '6px', md: '10px' },
                  borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
                  height: '16px',
                  flexShrink: 0,
                }}
              >
                <AccessTimeIcon
                  sx={{
                    fontSize: { xs: '12px', md: '13px' },
                    color: 'rgba(255, 255, 255, 0.85)',
                    opacity: 0.9,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: '11px', md: '12px' },
                    color: 'rgba(255, 255, 255, 0.92)',
                    fontWeight: 500,
                    letterSpacing: '0.2px',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {patientDetails.time}
                </Typography>
              </Box>
            )}

            {/* Fee Paid - Only for consultations */}
            {patientDetails.feePaid && (() => {
              const currencyData = getCurrencyObject(patientDetails.currency, true)
              const formattedPrice = new Intl.NumberFormat('en-IN').format(patientDetails.feePaid)
              const currencySymbol = currencyData?.symbol || ''
              
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    paddingLeft: { xs: '6px', md: '10px' },
                    borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '3px 7px',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.28) 0%, rgba(5, 150, 105, 0.22) 100%)',
                    borderRadius: '7px',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    boxShadow: '0 1px 3px rgba(16, 185, 129, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                    height: '16px',
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '10px', md: '11px' },
                      color: 'rgba(255, 255, 255, 0.95)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.2px',
                      lineHeight: 1,
                      marginRight: '4px',
                    }}
                  >
                    Fee
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '11px', md: '12px' },
                      color: '#ffffff',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.2px',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                      lineHeight: 1,
                    }}
                  >
                    {currencySymbol}{formattedPrice}
                  </Typography>
                </Box>
              )
            })()}
          </Box>
          </Box>
        </Box>
        )
      ) : (
        <GenericDisplayFlexColumn
          style={{
            paddingLeft: hasReportData ? '0' : '10px',
            flexShrink: 0,
            minWidth: 0,
            flex: 1,
          }}
        >
          <LiveSessionHeaderTop
            topTextData={topHeadingFirstText}
            onDebugClick={handleDebugModeClick}
          />
          <LiveSessionHeaderBottom bottomTextData={bottomHeadingText} />
        </GenericDisplayFlexColumn>
      )}
    </GenericDisplayFlex>
  )
}

export default LiveSessionHeaderDetails
